import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoyaltyBadge } from "@/components/app/LoyaltyBadge";
import { StatusBadge } from "@/components/app/StatusBadge";
import BatchPayDialog from "@/components/app/BatchPayDialog";
import PlanChangeDialog from "@/components/app/PlanChangeDialog";
import { formatBRL, formatDate, recurrenceLabel } from "@/lib/format";
import { QrCode, Users, ArrowsLeftRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const TIER_HEX = { bronze: "#A67C00", silver: "#52525B", gold: "#D4AF37", king: "#FDE08B" };

/**
 * PlansList — renders the logged-in client's main plan and all linked subaccounts.
 * Each plan card can be selected via checkbox; a sticky action bar at the bottom
 * lets the user pay one or many plans in a single PIX (batch).
 */
export default function PlansList() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [payOpen, setPayOpen] = useState(false);
  const [changeAccount, setChangeAccount] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({}); // user_id -> request

  const load = async () => {
    try {
      const [{ data: d }, changesRes] = await Promise.allSettled([
        api.get("/client/me/plans"),
        api.get("/client/plan-changes"),
      ]).then((arr) => arr.map((r) => r.status === "fulfilled" ? r.value : { data: null }));
      // If /client/me/plans is unavailable, fall back to /client/me single-account view
      if (!d) {
        try {
          const { data: me } = await api.get("/client/me");
          setData({ main: { ...me, effective_amount: me.plan_value, is_subaccount: false }, subaccounts: [], total_active_amount: me.plan_value });
        } catch { /* handled globally */ }
        return;
      }
      setData(d);
      const changes = changesRes?.data || [];
      // index pending changes by account user_id
      const pending = {};
      (changes || []).filter((c) => c.status === "pending" && !c.applied).forEach((c) => {
        pending[c.user_id] = c;
      });
      setPendingChanges(pending);
      // Default selection: only the main plan if it's near/overdue
      setSelected((prev) => {
        if (prev.size > 0) return prev;
        const initial = new Set();
        if (d?.main && ["proximo", "vencido"].includes(d.main.payment_status)) initial.add(d.main.id);
        return initial;
      });
    } catch { /* handled globally */ }
  };

  useEffect(() => { load(); }, []);

  const allPlans = useMemo(() => {
    if (!data) return [];
    return [data.main, ...(data.subaccounts || [])];
  }, [data]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const eligible = allPlans.filter((p) => p.status === "ativo" && Number(p.effective_amount) > 0);
    if (selected.size === eligible.length) setSelected(new Set());
    else setSelected(new Set(eligible.map((p) => p.id)));
  };

  const breakdown = useMemo(() => {
    return allPlans
      .filter((p) => selected.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.name,
        beneficiary: p.beneficiary,
        username: p.username,
        effective_amount: p.effective_amount,
      }));
  }, [selected, allPlans]);

  if (!data) return null;
  const total = breakdown.reduce((acc, b) => acc + Number(b.effective_amount || 0), 0);
  const hasSubs = (data.subaccounts || []).length > 0;

  return (
    <div className="space-y-4">
      {/* Main plan as hero card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <MainPlanCard
          plan={data.main}
          selected={selected.has(data.main.id)}
          pendingChange={pendingChanges[data.main.id]}
          onToggle={() => toggle(data.main.id)}
          onPayOnly={() => { setSelected(new Set([data.main.id])); setPayOpen(true); }}
          onChangePlan={() => setChangeAccount(data.main)}
        />
      </motion.div>

      {/* Subaccounts */}
      {hasSubs && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }} className="space-y-3">
          <div className="flex items-center justify-between px-1 mt-2">
            <div className="flex items-center gap-2">
              <Users size={16} weight="duotone" className="text-blue-600"/>
              <h2 className="font-display text-sm font-semibold tracking-tight">
                Planos vinculados <span className="text-zinc-500 font-normal">({data.subaccounts.length})</span>
              </h2>
            </div>
            <button
              onClick={selectAll}
              data-testid="plans-select-all"
              className="text-xs font-medium text-blue-600 hover:underline"
            >
              {selected.size === allPlans.filter((p) => p.status === "ativo").length ? "Limpar seleção" : "Selecionar todos"}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.subaccounts.map((sub) => (
              <SubPlanCard
                key={sub.id}
                plan={sub}
                selected={selected.has(sub.id)}
                pendingChange={pendingChanges[sub.id]}
                onToggle={() => toggle(sub.id)}
                onPayOnly={() => { setSelected(new Set([sub.id])); setPayOpen(true); }}
                onChangePlan={() => setChangeAccount(sub)}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Sticky pay bar (visible when at least 1 plan selected) */}
      {selected.size > 0 && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="sticky bottom-20 z-20"
          data-testid="batch-pay-bar"
        >
          <Card className="border-blue-200 bg-blue-600 text-white rounded-2xl p-4 card-soft flex items-center gap-3 shadow-lg">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-widest uppercase opacity-80 font-semibold">
                {selected.size} plano{selected.size !== 1 && "s"} selecionado{selected.size !== 1 && "s"}
              </div>
              <div className="font-display text-xl font-bold mt-0.5 tab-nums">{formatBRL(total)}</div>
            </div>
            <Button
              onClick={() => setPayOpen(true)}
              data-testid="batch-pay-btn"
              size="lg"
              className="bg-white text-blue-700 hover:bg-zinc-100 font-semibold"
            >
              <QrCode size={16} weight="bold" className="mr-1.5"/> Pagar agora
            </Button>
          </Card>
        </motion.div>
      )}

      <BatchPayDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        userIds={Array.from(selected)}
        breakdown={breakdown}
        onPaid={() => { setSelected(new Set()); load(); }}
      />

      <PlanChangeDialog
        open={!!changeAccount}
        onOpenChange={(v) => !v && setChangeAccount(null)}
        account={changeAccount}
        onApplied={() => { setChangeAccount(null); load(); }}
      />
    </div>
  );
}

const MainPlanCard = ({ plan, selected, onToggle, onPayOnly, onChangePlan, pendingChange }) => {
  return (
    <Card className="relative overflow-hidden bg-zinc-950 text-zinc-100 border-zinc-900 rounded-2xl p-6 card-soft" data-testid={`plan-main-${plan.id}`}>
      <div
        className="absolute -top-10 -right-10 w-48 h-48 rounded-full"
        style={{ background: `radial-gradient(circle, ${TIER_HEX[plan.loyalty_tier]}33 0%, transparent 70%)` }}
      />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <button
              type="button"
              onClick={onToggle}
              data-testid={`plan-toggle-${plan.id}`}
              aria-label="Selecionar plano"
              className={`mt-1 w-6 h-6 rounded-md border-2 flex-shrink-0 grid place-items-center transition-colors ${
                selected ? "bg-blue-600 border-blue-600" : "border-zinc-600 hover:border-zinc-400"
              }`}
            >
              {selected && <CheckIcon />}
            </button>
            <div className="min-w-0">
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-400">Plano principal</div>
              <div className="font-display text-xl mt-1 truncate">{plan.name}</div>
              <div className="text-xs text-zinc-400 mt-0.5">@{plan.username} · {recurrenceLabel[plan.recurrence] || plan.recurrence}</div>
            </div>
          </div>
          <LoyaltyBadge tier={plan.loyalty_tier} size="lg" />
        </div>
        <div className="metric-num text-4xl mt-4 tab-nums">{formatBRL(plan.effective_amount)}</div>
        {plan.effective_discount_pct > 0 && (
          <div className="text-xs text-emerald-300 mt-1">
            -{plan.effective_discount_pct}% fidelidade · valor base {formatBRL(plan.plan_value)}
          </div>
        )}
        <div className="h-px bg-white/10 my-5" />
        <div className="grid grid-cols-3 gap-3 text-center">
          <Stat label="Vencimento" value={formatDate(plan.current_due_date)} />
          <div>
            <div className="text-[10px] tracking-widest uppercase text-zinc-400">Status</div>
            <div className="mt-1.5"><StatusBadge status={plan.status === "ativo" ? plan.payment_status : plan.status} /></div>
          </div>
          <Stat label="Desconto" value={`${plan.effective_discount_pct}%`} accent />
        </div>
        <Button
          onClick={onPayOnly}
          data-testid={`plan-pay-${plan.id}`}
          variant="outline"
          className="mt-5 w-full bg-white/10 hover:bg-white/20 text-white border-white/20"
          disabled={Number(plan.effective_amount) <= 0 || plan.status !== "ativo"}
        >
          <QrCode size={14} className="mr-1.5"/> Pagar somente este
        </Button>
        <Button
          onClick={onChangePlan}
          data-testid={`plan-change-${plan.id}`}
          variant="ghost"
          className="mt-2 w-full text-amber-200 hover:text-amber-100 hover:bg-white/5"
        >
          <ArrowsLeftRight size={14} className="mr-1.5"/>
          {pendingChange ? `Troca pendente: ${pendingChange.new_plan_name}` : "Trocar plano"}
        </Button>
      </div>
    </Card>
  );
};

const SubPlanCard = ({ plan, selected, onToggle, onPayOnly, onChangePlan, pendingChange }) => {
  const stColor = plan.payment_status === "vencido" ? "rose"
    : plan.payment_status === "proximo" ? "amber"
    : plan.payment_status === "em_dia" ? "emerald" : "zinc";
  const isActive = plan.status === "ativo";
  return (
    <Card
      className={`relative rounded-2xl p-4 card-soft border transition-all ${
        selected ? "border-blue-500 ring-2 ring-blue-200" : "border-zinc-200"
      } ${!isActive ? "opacity-60" : ""}`}
      data-testid={`plan-sub-${plan.id}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          disabled={!isActive}
          data-testid={`plan-toggle-${plan.id}`}
          aria-label="Selecionar plano"
          className={`mt-0.5 w-5 h-5 rounded-md border-2 flex-shrink-0 grid place-items-center transition-colors ${
            selected ? "bg-blue-600 border-blue-600" : "border-zinc-300 hover:border-zinc-500"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {selected && <CheckIcon dark />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base font-semibold truncate">{plan.name}</div>
            {plan.beneficiary && (
              <span className="px-1.5 py-0.5 rounded text-[10px] tracking-widest uppercase font-semibold text-zinc-600 bg-zinc-100 border border-zinc-200">
                {plan.beneficiary}
              </span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            @{plan.username} · {recurrenceLabel[plan.recurrence] || plan.recurrence}
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest bg-${stColor}-50 text-${stColor}-700 border border-${stColor}-200`}>
          {plan.status !== "ativo" ? plan.status : (plan.payment_status === "em_dia" ? "Em dia" : plan.payment_status === "proximo" ? "Próximo" : plan.payment_status === "vencido" ? "Vencido" : "")}
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="metric-num text-2xl tab-nums">{formatBRL(plan.effective_amount)}</div>
          {plan.effective_discount_pct > 0 && (
            <div className="text-[10px] text-emerald-700 mt-0.5">-{plan.effective_discount_pct}% · base {formatBRL(plan.plan_value)}</div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-widest uppercase text-zinc-400">Vence em</div>
          <div className="font-display text-sm font-semibold mt-0.5">{formatDate(plan.current_due_date)}</div>
        </div>
      </div>

      <Button
        onClick={onPayOnly}
        disabled={Number(plan.effective_amount) <= 0 || !isActive}
        data-testid={`plan-pay-${plan.id}`}
        variant="outline"
        size="sm"
        className="mt-3 w-full"
      >
        <QrCode size={12} className="mr-1.5"/> Pagar somente este
      </Button>
      <Button
        onClick={onChangePlan}
        data-testid={`plan-change-${plan.id}`}
        variant="ghost"
        size="sm"
        className="mt-1.5 w-full text-blue-700 hover:bg-blue-50"
      >
        <ArrowsLeftRight size={12} className="mr-1.5"/>
        {pendingChange ? `Troca pendente: ${pendingChange.new_plan_name}` : "Trocar plano"}
      </Button>
    </Card>
  );
};

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] tracking-widest uppercase text-zinc-400">{label}</div>
    <div className={`font-display text-base mt-1 ${accent ? "text-emerald-300" : ""}`}>{value}</div>
  </div>
);

const CheckIcon = ({ dark }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 12l5 5L20 7" stroke={dark ? "#fff" : "#fff"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
